import React from "react";
import { Link } from "react-router-dom";
import {
  Clapperboard,
  Users,
  Handshake,
  Film,
} from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { formHref } from "@/constants/formAnchors";
import { scrollToFormTop } from "@/lib/scrollToForm";

const CARDS = [
  {
    id: "projects",
    to: "/projects",
    icon: Clapperboard,
    label: "Projects",
    desc: "Original SoKno Productions films in development, production, and post.",
    status: "Explore",
    cta: "View Projects",
  },
  {
    id: "casting",
    to: formHref("talent"),
    icon: Users,
    label: "Casting",
    desc: "General talent submissions for future SoKno Productions projects.",
    status: "Open Submissions",
    cta: "Submit Talent",
  },
  {
    id: "crew",
    to: formHref("crew"),
    icon: Film,
    label: "Crew & Production",
    desc: "Crew, vendors, and production professionals for Dallas and Texas film work.",
    status: "Open Submissions",
    cta: "Submit Crew & Production",
  },
  {
    id: "partner",
    to: formHref("partner"),
    icon: Handshake,
    label: "Partner With SoKno",
    desc: "Investors, sponsors, financing, distribution, and strategic partners.",
    status: "Now Connecting",
    cta: "Submit Partnership Interest",
  },
];

function hashFromTo(to) {
  const i = String(to).indexOf("#");
  return i >= 0 ? String(to).slice(i + 1) : null;
}

function pathFromTo(to) {
  const i = String(to).indexOf("#");
  return i >= 0 ? String(to).slice(0, i) : String(to);
}

function Card({ card, index }) {
  const Icon = card.icon;
  const r = useReveal();
  const anchorId = hashFromTo(card.to);
  const pathname = pathFromTo(card.to);
  return (
    <Link
      ref={r.ref}
      to={card.to}
      data-testid={`feature-card-${card.id}`}
      className={`panel block group ${r.className}`}
      style={{
        textDecoration: "none",
        transitionDelay: `${index * 80}ms`,
        opacity: card.muted ? 0.92 : 1,
      }}
      onClick={() => {
        if (anchorId) {
          window.setTimeout(() => {
            scrollToFormTop(anchorId, { behavior: "auto", pathname });
          }, 0);
        }
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          border: "1px solid var(--sokno-line-strong)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--sokno-gold)",
          background: "rgba(214,175,99,0.04)",
        }}
      >
        <Icon size={20} strokeWidth={1.4} />
      </div>

      <div className="mt-5 card-meta">{card.status}</div>
      <h3
        className="font-display mt-2"
        style={{
          color: "var(--sokno-cream)",
          fontSize: 22,
          fontWeight: 500,
          letterSpacing: "-0.005em",
          lineHeight: 1.2,
        }}
      >
        {card.label}
      </h3>
      <p
        className="mt-3"
        style={{ color: "var(--sokno-cream-dim)", fontSize: 14, lineHeight: 1.65 }}
      >
        {card.desc}
      </p>
      <span
        className="link-gold mt-6 inline-flex"
        aria-hidden="true"
        data-testid={`feature-card-cta-${card.id}`}
      >
        {card.cta}
        {!card.muted && <span aria-hidden>→</span>}
      </span>
    </Link>
  );
}

export default function FeatureCards() {
  return (
    <section
      className="relative section-tone section-tone-talent"
      data-testid="feature-cards-section"
      style={{ paddingTop: 40, paddingBottom: 100 }}
    >
      <div className="container-x">
        <div
          className="grid gap-5"
          data-testid="feature-cards-grid"
          style={{
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          }}
        >
          {CARDS.map((c, i) => (
            <Card key={c.id} card={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
